import React, { useState } from 'react';
import { getUILoc, getCardName, groupCards } from '../../../utils/cardUtils.js';
import { CardIllustration } from '../../Card.js';
import CardDetail from '../../CardDetail.js';

const DiplomacyEvent = ({ activeRun, cardDefs, executeDiplomacyExchange, requestDiplomacyReroll, goToNextNodeSelection }) => {
  const [selectedPresentedCardId, setSelectedPresentedCardId] = useState(null);
  const [selectedPlayerCardIds, setSelectedPlayerCardIds] = useState([]);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [eventResultMessage, setEventResultMessage] = useState(null);
  const [inspectCard, setInspectCard] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleReroll = () => {
    if (!requestDiplomacyReroll) return;
    const res = requestDiplomacyReroll();
    if (!res.success) {
      setErrorMessage(res.reason);
    } else {
      setEventResultMessage(null);
    }
  };

  const presentedIds = activeRun.eventState.presented || [];

  const getCardValue = (id) => {
    if (id === 1000 || id === 33) return 3; // 芸術 or マネー
    const def = cardDefs[id];
    if (!def) return 1;
    if (def.required_scale < 8) return 1;
    if (def.required_scale < 30) return 2;
    return 3;
  };

  const handleOpenExchange = (id) => {
    setSelectedPresentedCardId(id);
    setSelectedPlayerCardIds([]);
    setShowExchangeModal(true);
  };

  const handleExchange = () => {
    // 供出カードが0枚の場合、まだ警告が出ていなければまず警告を出す
    if (selectedPlayerCardIds.length === 0 && !errorMessage) {
        setErrorMessage(getUILoc('YONOTSUNE.ERRORS.SELECT_1_2_CARDS') || 'No cards selected. Attempt free negotiation?');
        return;
    }

    const res = executeDiplomacyExchange(selectedPresentedCardId, selectedPlayerCardIds);
    if (res.success) {
      if (res.outcome === 'breakdown') {
        setEventResultMessage(getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.RESULT_BREAKDOWN'));
      } else {
        setEventResultMessage(res.detail);
      }
      setSelectedPresentedCardId(null);
      setSelectedPlayerCardIds([]);
      setShowExchangeModal(false);
      setErrorMessage(null);
    } else {
      setErrorMessage(res.reason);
    }
  };

  const togglePlayerCardSelection = (id) => {
    setSelectedPlayerCardIds(prev => {
        if (prev.includes(id)) {
            const next = [...prev];
            const idx = next.lastIndexOf(id);
            next.splice(idx, 1);
            return next;
        } else {
            if (prev.length < 2) {
                return [...prev, id];
            } else {
                return [prev[1], id]; // 3枚目は古い方を押し出し
            }
        }
    });
  };

  const groupedPlayerDeck = groupCards(activeRun.playerDeck);

  const renderErrorModal = () => {
    if (!errorMessage) return null;
    
    // エラーが「カード未選択」に関連するものか判定
    const isWarning = errorMessage.includes(getUILoc('YONOTSUNE.ERRORS.SELECT_MAX_2_CARDS')) || 
                      errorMessage.includes(getUILoc('YONOTSUNE.ERRORS.SELECT_1_2_CARDS')) ||
                      selectedPlayerCardIds.length === 0;

    return (
      <div className="yonotsune-modal-overlay" style={{ zIndex: 4000 }} onClick={() => setErrorMessage(null)}>
        <div className="choice-prompt modal-content" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-danger">
            {isWarning ? getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.NAME') : (getUILoc('YONOTSUNE.HUD.MATCH_ERROR') || 'Error')}
          </h3>
          <p>{errorMessage}</p>
          <div className="choice-options" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            {isWarning && (
              <button className="yonotsune-btn green-btn" style={{ flex: 1 }} onClick={handleExchange}>
                {getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.EXECUTE')}
              </button>
            )}
            <button className="yonotsune-btn" style={{ flex: 1 }} onClick={() => setErrorMessage(null)}>
              {getUILoc('BUTTONS.CLOSE') || 'Close'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderExchangeModal = () => {
    if (!showExchangeModal || !selectedPresentedCardId) return null;

    const presentedVal = getCardValue(selectedPresentedCardId);
    const playerTotalVal = selectedPlayerCardIds.reduce((sum, id) => sum + getCardValue(id), 0);

    return (
      <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 3000 }} onClick={() => setShowExchangeModal(false)}>
        <div className="player-details-modal pile-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
          <div className="player-details-header">
            <h3>{getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.NAME')} - {getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.PLAYER_OFFER')}</h3>
            <button className="close-button" onClick={() => setShowExchangeModal(false)}>×</button>
          </div>
          <div className="player-details-content">
            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span>{getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.PRESENTED')}: <strong>{getCardName(selectedPresentedCardId)}</strong></span>
                    <span className="text-danger">V:{presentedVal}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
                    <span>{getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.PLAYER')}:</span>
                    <span className="text-heal">V:{playerTotalVal}</span>
                </div>
            </div>

            <div className="compact-scroll-area">
              <div className="presented-grid">
                {groupedPlayerDeck.map((item, index) => {
                  const val = getCardValue(item.id);
                  const selectedCount = selectedPlayerCardIds.filter(id => id === item.id).length;
                  const isSelected = selectedCount > 0;
                  
                  return (
                    <div 
                      key={index} 
                      className={`pile-card-group clickable ${isSelected ? 'active' : ''}`}
                      onClick={() => togglePlayerCardSelection(item.id)}
                      style={{ border: isSelected ? '2px solid #4caf50' : '1px solid rgba(255,255,255,0.1)', background: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'transparent', padding: '5px' }}
                    >
                      <div className="pile-card-illustration-wrapper">
                        <CardIllustration cardId={item.id} isMini={true} />
                        <div className="pile-card-count-badge">×{item.count}</div>
                        {isSelected && <div className="pile-card-prob-badge" style={{ background: '#4caf50' }}>{selectedCount}</div>}
                        <div className="card-val" style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '0.6rem', background: 'rgba(0,0,0,0.6)', padding: '1px 3px', borderRadius: '2px' }}>V:{val}</div>
                        <button className="inspect-btn-mini" style={{ position: 'absolute', bottom: '2px', right: '2px', zIndex: 5 }} onClick={(e) => { e.stopPropagation(); setInspectCard(cardDefs[item.id]); }}>🔍</button>
                      </div>
                      <div className="pile-card-name" style={{ fontSize: '0.7rem' }}>{getCardName(item.id)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="action-row" style={{ marginTop: '20px' }}>
                <button 
                    className="yonotsune-btn green-btn"
                    onClick={handleExchange}
                    style={{ flex: 1 }}
                >
                    {getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.EXECUTE')}
                </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="event-resolver diplomacy-event">
      <h2 style={{ fontSize: '1.2rem', marginBottom: '5px' }}>{getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.NAME')}</h2>
      <p className="resolver-intro" style={{ fontSize: '0.8rem', marginBottom: '10px' }} dangerouslySetInnerHTML={{ __html: getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.INTRO') }} />

      {eventResultMessage && <div className="event-result-banner" style={{ padding: '5px 15px', marginBottom: '10px' }}>{eventResultMessage}</div>}

      <div className="diplomacy-interface">
        <div className="presented-cards-section" style={{ background: 'transparent', border: 'none' }}>
          <h3 style={{ fontSize: '0.85rem', textAlign: 'center', marginBottom: '15px' }}>{getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.PRESENTED')}</h3>
          <div className="presented-grid" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px' }}>
            {presentedIds.map((id, i) => {
              const val = getCardValue(id);
              return (
                <div 
                  key={i} 
                  className="pile-card-group clickable"
                  onClick={() => handleOpenExchange(id)}
                  style={{ width: '80px', border: '1px solid rgba(33, 150, 243, 0.3)', background: 'rgba(0,0,0,0.4)', padding: '10px' }}
                >
                  <div className="pile-card-illustration-wrapper">
                    <CardIllustration cardId={id} isMini={true} />
                    <div className="pile-card-count-badge" style={{ background: '#f44336' }}>V:{val}</div>
                    <button className="inspect-btn-mini" style={{ position: 'absolute', bottom: '2px', right: '2px', zIndex: 5 }} onClick={(e) => { e.stopPropagation(); setInspectCard(cardDefs[id]); }}>🔍</button>
                  </div>
                  <div className="pile-card-name" style={{ fontSize: '0.8rem', marginTop: '8px' }}>{getCardName(id)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="action-row" style={{ marginTop: '30px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
        <button className="yonotsune-btn secondary-btn" onClick={handleReroll}>
          {getUILoc('YONOTSUNE.EVENTS.DIPLOMACY.REROLL_BTN') || '要求 (提示変更 - 意識-3)'}
        </button>
        <button className="yonotsune-btn" onClick={() => { goToNextNodeSelection(); setEventResultMessage(null); }}>
          {getUILoc('YONOTSUNE.EVENTS.NEXT_NODE')}
        </button>
      </div>

      {renderExchangeModal()}
      {renderErrorModal()}

      {inspectCard && (
        <CardDetail 
          card={inspectCard} 
          cardDefs={cardDefs} 
          onClose={() => setInspectCard(null)} 
        />
      )}
    </div>
  );
};

export default DiplomacyEvent;
